import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { api, formatINR, readUTM, readCookie } from "@/lib/api";
import { PublicHeader, PublicFooter } from "@/components/PublicShell";
import { toast } from "sonner";
import { loadRazorpay, loadCashfree, trackPixelEvent } from "@/lib/payments";
import { ArrowRight, ShieldCheck, CaretLeft, Info, Repeat, Heart } from "@phosphor-icons/react";

export default function DonatePage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const presetAmount = Number(params.get("amount") || 0);
  const monthlyParam = params.get("monthly") === "1";

  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [publicSettings, setPublicSettings] = useState({ gateways: {} });

  const amount = presetAmount || 3000;
  const [monthly] = useState(monthlyParam);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [claim80g, setClaim80g] = useState(false);
  const [pan, setPan] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/campaigns/${slug}`).then((r) => setC(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/settings/public").then((r) => setPublicSettings(r.data || {})).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (anonymous) { setClaim80g(false); setPan(""); setAddress(""); }
  }, [anonymous]);

  const activeGateways = useMemo(() => Object.keys(publicSettings.gateways || {}).filter((g) => publicSettings.gateways[g]), [publicSettings]);
  // Both gateways can be enabled at once: monthly donations always go
  // through Razorpay (the only one with subscription support here);
  // one-time donations prefer Cashfree when it's enabled, falling back to
  // Razorpay otherwise.
  const gateway = monthly
    ? (activeGateways.includes("razorpay") ? "razorpay" : null)
    : (activeGateways.includes("cashfree") ? "cashfree" : (activeGateways.includes("razorpay") ? "razorpay" : null));

  if (loading) return <div className="min-h-screen grid place-items-center text-[#56624F]">Loading...</div>;
  if (!c) return <div className="min-h-screen grid place-items-center">Campaign not found.</div>;

  if (!presetAmount) {
    return (
      <>
        <PublicHeader />
        <div className="max-w-xl mx-auto p-8 text-center">
          <p className="text-[#56624F] mb-4">No donation amount selected.</p>
          <Link to={`/campaigns/${c.slug}`} className="btn-primary">Choose an amount</Link>
        </div>
        <PublicFooter />
      </>
    );
  }

  const handlePay = async () => {
    if (!anonymous && !name.trim()) return toast.error("Please enter your full name");
    // Email optional for anonymous, required otherwise
    if (!anonymous && (!email.trim() || !email.includes("@"))) return toast.error("Please enter a valid email");
    if (anonymous && email.trim() && !email.includes("@")) return toast.error("Please enter a valid email (or leave blank)");
    if (!anonymous && phone.replace(/\D/g, "").length < 10) return toast.error("Please enter a valid 10-digit phone number");
    if (claim80g) {
      if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan)) return toast.error("Valid PAN required for 80G receipt");
      if (!address.trim()) return toast.error("Please enter your address for the 80G receipt");
    }
    if (!gateway) {
      return toast.error(monthly
        ? "Monthly donations require Razorpay, which isn't enabled yet. Please try a one-time donation instead."
        : "No payment gateway enabled by the organisation yet. Please use the UPI/Bank options on the campaign page.");
    }

    setSubmitting(true);
    try {
      // Anonymous without email: use a placeholder for receipt skip
      const donorEmail = email.trim() || (anonymous ? "anonymous@seva-animals.org" : "");
      const payload = {
        campaign_id: c.id,
        amount,
        donor_name: anonymous ? "Anonymous" : name,
        donor_email: donorEmail,
        donor_phone: anonymous ? "" : phone,
        gateway,
        round_up: false,
        pan: claim80g ? pan : null,
        claim_80g: !anonymous && claim80g,
        address_80g: (!anonymous && claim80g) ? address : null,
        utm: readUTM(),
        fbp: readCookie("_fbp"),
        fbc: readCookie("_fbc"),
        anonymous,
        monthly: monthly && gateway === "razorpay",
      };
      const { data } = await api.post("/donations/create", payload);
      trackPixelEvent("InitiateCheckout", { value: amount, currency: "INR" }, data.donation_id);

      if (gateway === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) return toast.error("Unable to load Razorpay.");
        const options = {
          key: data.key_id,
          name: "SEVA Animal Welfare",
          description: (monthly ? "Monthly Donation: " : "") + c.title,
          prefill: { name: anonymous ? "" : name, email: donorEmail, contact: phone },
          theme: { color: "#4FAC39" },
          modal: { ondismiss: () => { toast.info("Payment cancelled"); api.post(`/donations/${data.donation_id}/mark-failed`).catch(() => {}); } },
        };
        if (payload.monthly && data.subscription_id) {
          options.subscription_id = data.subscription_id;
          options.handler = async (resp) => {
            try {
              await api.post("/donations/razorpay/verify", {
                subscription_id: resp.razorpay_subscription_id || data.subscription_id,
                payment_id: resp.razorpay_payment_id,
                signature: resp.razorpay_signature,
              });
              trackPixelEvent("Subscribe", { value: amount, currency: "INR" }, data.donation_id);
              trackPixelEvent("Purchase", { value: amount, currency: "INR" }, data.donation_id);
              nav(`/donation/success/${data.donation_id}`);
            } catch (e) { toast.error("Payment verification failed."); }
          };
        } else {
          options.order_id = data.order_id;
          options.amount = data.amount;
          options.currency = data.currency;
          options.handler = async (resp) => {
            try {
              await api.post("/donations/razorpay/verify", {
                order_id: resp.razorpay_order_id,
                payment_id: resp.razorpay_payment_id,
                signature: resp.razorpay_signature,
              });
              trackPixelEvent("Donate", { value: amount, currency: "INR" }, data.donation_id);
              trackPixelEvent("Purchase", { value: amount, currency: "INR" }, data.donation_id);
              nav(`/donation/success/${data.donation_id}`);
            } catch (e) { toast.error("Payment verification failed."); }
          };
        }
        new window.Razorpay(options).open();
      } else if (gateway === "cashfree") {
        const ok = await loadCashfree();
        if (!ok) return toast.error("Unable to load Cashfree.");
        const mode = data.mode === "live" ? "production" : "sandbox";
        const cashfree = window.Cashfree({ mode });
        cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          redirectTarget: "_self",
          returnUrl: `${window.location.origin}${process.env.PUBLIC_URL}/donation/success/${data.donation_id}`,
        });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail ? String(detail) : (e?.message || "Unable to start donation"));
    } finally { setSubmitting(false); }
  };

  const fieldDisabled = anonymous;

  return (
    <>
      <PublicHeader />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-8 md:py-14">
        <Link to={`/campaigns/${c.slug}`} className="inline-flex items-center gap-1 text-sm text-[#56624F] hover:text-[#4FAC39] mb-4" data-testid="back-to-campaign"><CaretLeft size={16} /> Back to campaign</Link>

        <div className="seva-card flex items-center gap-4 mb-6">
          {c.images?.[0] && <img src={c.images[0]} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#2F7A4A] font-semibold">You&rsquo;re donating {monthly ? "monthly " : ""}<strong>{formatINR(amount)}</strong> to</div>
            <h1 className="font-display font-bold text-lg md:text-xl line-clamp-2">{c.title}</h1>
            {monthly && <div className="text-xs text-[#4FAC39] mt-1 inline-flex items-center gap-1"><Repeat size={12} weight="fill" /> Monthly Donation</div>}
          </div>
        </div>

        <div className="space-y-5">
          <h2 className="font-display font-bold text-2xl">Your details</h2>

          <label className="flex items-start gap-3 seva-card !bg-[#FBF9F6] cursor-pointer">
            <input type="checkbox" className="mt-1" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} data-testid="donation-anonymous" />
            <div>
              <div className="font-semibold text-[#1F2A1C]">Donate as Anonymous</div>
              <div className="text-xs text-[#56624F] mt-0.5">Your details won&rsquo;t be shown anywhere. You may still enter an email below to receive your donation receipt &mdash; we won&rsquo;t use it for anything else.</div>
            </div>
          </label>

          <div className="grid md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-sm font-semibold mb-1">Full name {!anonymous && "*"}</div>
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={fieldDisabled} className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white disabled:bg-[#F3F1EC] disabled:text-[#98A38F]" data-testid="donor-name" />
            </label>
            <label className="block">
              <div className="text-sm font-semibold mb-1">
                Email {anonymous ? <span className="font-normal text-xs text-[#56624F]">(optional &mdash; to receive receipt)</span> : "*"}
              </div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={anonymous ? "(optional, to get your receipt)" : ""}
                className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white" data-testid="donor-email" />
            </label>
            <label className="block md:col-span-2">
              <div className="text-sm font-semibold mb-1">Phone {!anonymous && "*"}</div>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={fieldDisabled} className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white disabled:bg-[#F3F1EC] disabled:text-[#98A38F]" data-testid="donor-phone" />
            </label>
          </div>

          <div className={"seva-card !bg-[#EEF7EA] border-[#4FAC39]/30 " + (fieldDisabled ? "opacity-60" : "")}>
            <label className={"flex items-start gap-3 " + (fieldDisabled ? "cursor-not-allowed" : "cursor-pointer")}>
              <input type="checkbox" checked={claim80g} onChange={(e) => setClaim80g(e.target.checked)} disabled={fieldDisabled} className="mt-1" data-testid="claim-80g" />
              <div className="flex-1">
                <div className="font-semibold text-[#1F2A1C] flex items-center gap-2"><ShieldCheck size={18} weight="fill" className="text-[#2F7A4A]" /> Claim 80G tax deduction</div>
                <p className="text-sm text-[#56624F] mt-1">Get a separate 80G-compliant tax receipt. PAN &amp; address required.</p>
              </div>
            </label>
            {claim80g && !fieldDisabled && (
              <div className="grid md:grid-cols-2 gap-3 mt-4">
                <label className="block">
                  <div className="text-sm font-semibold mb-1">PAN Number *</div>
                  <input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} maxLength={10} placeholder="AAAAA0000A" className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white font-mono" data-testid="donor-pan" />
                </label>
                <label className="block">
                  <div className="text-sm font-semibold mb-1">Address *</div>
                  <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="City, State, PIN" className="w-full px-4 py-3 rounded-xl border border-[#DCE5D4] bg-white" data-testid="donor-address" />
                </label>
              </div>
            )}
          </div>

          <div className="seva-card space-y-2">
            <h3 className="font-display font-bold">Order Summary</h3>
            <div className="flex justify-between text-sm"><span className="text-[#56624F]">Amount</span><span className="font-semibold">{formatINR(amount)}{monthly && "/mo"}</span></div>
            {claim80g && !anonymous && pan && <div className="flex justify-between text-sm"><span className="text-[#56624F]">80G receipt</span><span className="text-[#2F7A4A]">Included (PAN: {pan})</span></div>}
            <div className="flex justify-between text-sm"><span className="text-[#56624F]">Donor</span><span>{anonymous ? "Anonymous" : name || "—"}</span></div>
            {monthly && <div className="flex justify-between text-sm"><span className="text-[#56624F]">Frequency</span><span className="text-[#4FAC39]">Monthly Donation</span></div>}
            <div className="flex justify-between text-sm"><span className="text-[#56624F]">Payment</span><span className="uppercase font-semibold text-[#4FAC39]">{gateway || "Not available"}</span></div>
          </div>

          {!gateway && (
            <div className="seva-card bg-[#FFF8EC] border-[#E4C87A]">
              <div className="flex items-start gap-2 text-sm text-[#7A5E00]"><Info size={18} className="shrink-0 mt-0.5" /> No payment gateway is enabled yet. Please try the UPI/Bank options on the campaign page, or come back in a few minutes.</div>
            </div>
          )}

          <button onClick={handlePay} disabled={submitting || !gateway} className="btn-primary w-full !py-4 text-base tracking-wide uppercase disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2" data-testid="donation-checkout-btn">
            <Heart size={20} weight="fill" /> {submitting ? "Processing..." : <>Donate {formatINR(amount)}{monthly && "/mo"} <ArrowRight size={18} /></>}
          </button>
          <p className="text-xs text-center text-[#56624F]">100% secure payments. 80G tax benefits apply.</p>
        </div>
      </section>
      <PublicFooter />
    </>
  );
}
